import { Undo2, AlertTriangle } from 'lucide-react';
import { Modal } from '../Modal';

export interface ResultadoDevolucao {
  numero?: number;
  serie?: string;
  chave?: string;
  protocolo?: string | null;
  cStat?: string | null;
  estoque?: Array<{ codigo: string; descricao: string; quantidade: number; estoque_depois: number }>;
  estoque_ignorados?: Array<{ codigo: string; descricao: string; motivo?: string }>;
}

interface Props {
  resultado: ResultadoDevolucao;
  // Nota que foi desfeita (a origem), pra fechar o par na tela.
  origemRotulo: 'NFC-e' | 'NF-e' | 'nota de entrada';
  origemNumero: number | string;
  onFechar: () => void;
  // [DA-559] A devolucao ao FORNECEDOR anda pro outro lado: o estoque SAI. O modal e o mesmo (a
  // prova da SEFAZ e identica), mas dizer "voltou ao estoque" com sinal '+' sobre uma mercadoria
  // que foi embora seria mentira na tela que serve justamente de comprovante.
  sentido?: 'entrada' | 'saida';
}

// Resultado da devolucao em MODAL, nao em toast ([DA-443]). O toast sumia antes de dar tempo de
// ler — e isso e o desfecho de uma acao irreversivel que falou com a SEFAZ. A prova (numero da
// nota + protocolo) e o que voltou ao estoque precisam ficar na tela ate alguem fechar.
// Quantidade com ate 3 casas, sem zeros a toa: 1 e "1", 3.5 e "3,5" (o estoque e fracionado).
function qtd(n: number): string {
  return Number(n).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

export function ModalResultadoDevolucao({ resultado, origemRotulo, origemNumero, onFechar, sentido = 'entrada' }: Props) {
  const itens = resultado.estoque ?? [];
  const ignorados = resultado.estoque_ignorados ?? [];
  const saida = sentido === 'saida';
  const numOrigem = typeof origemNumero === 'number' ? String(origemNumero).padStart(6, '0') : origemNumero;

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={saida ? 'Devolução ao fornecedor emitida' : 'Devolução emitida'}
      largura={520}
      compacto
      rodape={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn-primario" onClick={onFechar} autoFocus>Fechar</button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>

        {/* Destaque positivo = amarelo + preto (nunca verde — daltonismo, ver user_daltonismo) */}
        <div style={{ background: '#fde68a', color: '#000', borderRadius: 6, padding: 10, lineHeight: 1.5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 2 }}>
            <Undo2 size={15} /> NF-e {String(resultado.numero ?? '').padStart(6, '0')} autorizada na SEFAZ
          </div>
          <div style={{ fontSize: 12 }}>
            Série {resultado.serie ?? '—'} · {saida ? 'devolve a' : 'desfaz a'} {origemRotulo} {numOrigem}
          </div>
        </div>

        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
          <div><strong>Protocolo:</strong> <span className="cel-num">{resultado.protocolo || '—'}</span></div>
          {resultado.chave && (
            <div style={{ wordBreak: 'break-all' }}>
              <strong>Chave:</strong>{' '}
              <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{resultado.chave}</span>
            </div>
          )}
        </div>

        {itens.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{saida ? 'Saiu do estoque' : 'Voltou ao estoque'}</div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              {itens.map((it, i) => (
                <div key={it.codigo + i} style={{
                  display: 'flex', gap: 8, alignItems: 'baseline', padding: '6px 10px', fontSize: 12,
                  borderTop: i === 0 ? undefined : '1px solid var(--border)',
                  background: i % 2 ? 'var(--panel-bg)' : 'var(--primary-soft)',
                }}>
                  <span className="cel-num" style={{ flex: '0 0 auto', color: 'var(--text-muted)' }}>{it.codigo}</span>
                  <span style={{ flex: '1 1 auto' }}>{it.descricao}</span>
                  <span className="cel-num" style={{ flex: '0 0 auto', fontWeight: 700 }}>{saida ? '−' : '+'}{qtd(it.quantidade)}</span>
                  <span className="cel-num" style={{ flex: '0 0 auto', color: 'var(--text-muted)' }}>= {qtd(it.estoque_depois)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* O que NAO voltou tem que gritar: a nota ja saiu, mas o estoque daquele item ficou pra tras. */}
        {ignorados.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#f97316', color: '#000', border: '1px solid #c2410c', borderRadius: 6, padding: 10, fontSize: 12, lineHeight: 1.5 }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <strong>
                {ignorados.length === 1 ? 'Um item não' : `${ignorados.length} itens não`}{' '}
                {saida ? (ignorados.length === 1 ? 'saiu' : 'saíram') : (ignorados.length === 1 ? 'voltou' : 'voltaram')} do estoque
              </strong>{' '}
              — {ignorados.map((g) => `${g.codigo} ${g.descricao}${g.motivo ? ` (${g.motivo})` : ''}`).join(' · ')}.
              A nota fiscal já foi autorizada; ajuste esse estoque na mão.
            </div>
          </div>
        )}

        {itens.length === 0 && ignorados.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {saida ? 'Nenhum item saiu do estoque.' : 'Nenhum item foi devolvido ao estoque.'}
          </div>
        )}

      </div>
    </Modal>
  );
}
